import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getCookie, removeCookie } from '../utils/cookies';
import './Signup.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const Signup = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const isInvitedFlow = searchParams.get('invitation') === 'true';
  const invitationToken = searchParams.get('token');
  const invitationEmail = searchParams.get('email');
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: isInvitedFlow ? invitationEmail : '',
    password: '',
    confirmPassword: '',
    societyName: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');


  useEffect(() => {
    // Clean up cookies on unmount
    return () => {
      if (isInvitedFlow) {
        removeCookie('invitationToken');
        removeCookie('invitationEmail');
      }
    };
  }, [isInvitedFlow]);

  useEffect(() => {
    // Check cookies on mount
    if (isInvitedFlow) {
      const token = getCookie('invitationToken');
      const email = getCookie('invitationEmail');
      console.log('Cookies on mount:', { token, email });
    }
  }, [isInvitedFlow]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isInvitedFlow) {
        if (!invitationToken) {
          throw new Error('Invalid invitation. Please use the invitation link sent to your email.');
        }

        const signupData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          confirm_password: formData.confirmPassword,
          invitation_token: invitationToken
        };

        const response = await fetch(`${API_URL}/api/v1/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(signupData),
        });

        const data = await response.json();
        console.log('Registration response:', data);

        if (!response.ok) {
          throw new Error(data.message || 'Registration failed');
        }

        navigate('/login', { 
          state: { 
            message: 'Account created successfully! Please login to continue.' 
          }
        });
      } else {
        // Step 1: Register the user
        const registerData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          confirm_password: formData.confirmPassword
        };

        const registerResponse = await fetch(`${API_URL}/api/v1/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(registerData),
        });

        const registerResult = await registerResponse.json();

        if (!registerResponse.ok) {
          throw new Error(registerResult.message || 'Registration failed');
        }

        console.log('Registration successful:', registerResult);

        // Step 2: Login to get access token
        const loginData = {
          email: formData.email,
          password: formData.password
        };

        const loginResponse = await fetch(`${API_URL}/api/v1/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(loginData),
        });

        const loginResult = await loginResponse.json();

        if (!loginResponse.ok) {
          throw new Error(loginResult.message || 'Login failed');
        }

        const accessToken = loginResult.access_token;
        console.log('Login successful, got access token');

        // Step 3: Create organization
        const createOrgData = {
          Name: formData.societyName
        };

        const orgResponse = await fetch(`${API_URL}/api/v1/auth/orgs?access_token=${encodeURIComponent(accessToken)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(createOrgData),
        });

        const orgResult = await orgResponse.json();

        if (!orgResponse.ok) {
          throw new Error(orgResult.message || 'Failed to create organization');
        }

        const orgId = orgResult.data?.ID || orgResult.data?.Id;
        console.log('Organization created:', orgId);

        // Step 4: Assign admin role to the user using predefined role ID
        const adminRoleId = process.env.REACT_APP_ADMIN_ROLE_ID || 'role_aPINOluBSTlZ5oEO';
        // Step 5: Assign admin role to the user
        const assignRoleData = {
          RoleIds: [adminRoleId]
        };

        const assignRoleResponse = await fetch(`${API_URL}/api/v1/auth/account/orgs/${orgId}/roles?access_token=${encodeURIComponent(accessToken)}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(assignRoleData),
        });

        if (!assignRoleResponse.ok) {
          const assignRoleResult = await assignRoleResponse.json();
          throw new Error(assignRoleResult.message || 'Failed to assign admin role');
        }

        console.log('Admin role assigned successfully');

        // Step 6: Fetch profile data to populate organizations
        const profileResponse = await fetch(
          `${API_URL}/api/v1/auth/profile?access_token=${encodeURIComponent(accessToken)}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        if (!profileResponse.ok) {
          throw new Error('Failed to fetch profile data');
        }

        const profileData = await profileResponse.json();

        // Format and store profile data
        const formattedData = {
          name: profileData.Profile.FullName || `${profileData.Profile.FirstName} ${profileData.Profile.LastName}`.trim(),
          email: profileData.Profile.Email?.[0]?.Value || '',
          id: profileData.Profile.ID,
          uid: profileData.Profile.Uid,
          provider: profileData.Profile.Provider,
          Profile: profileData.Profile,
          Orgs: profileData.Orgs.map(org => ({
            OrgId: org.OrgId,
            Name: org.Name || org.OrgName,
            Permissions: org.Permissions,
            Policies: org.Policies || null
          }))
        };

        // Store all necessary data in localStorage
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('profileData', JSON.stringify(formattedData));
        
        // Store societies and set the newly created org as selected
        if (formattedData.Orgs && formattedData.Orgs.length > 0) {
          localStorage.setItem('societies', JSON.stringify(formattedData.Orgs));
          localStorage.setItem('societyId', formattedData.Orgs[0].OrgId);
        }

        console.log('Profile data stored successfully');

        // Update authentication state
        login(accessToken);

        // Dispatch event to notify Navbar to reload societies
        window.dispatchEvent(new Event('societyChanged'));

        // Small delay to ensure all state updates are processed
        await new Promise(resolve => setTimeout(resolve, 100));
        
        navigate('/dashboard', { 
          state: { 
            message: 'Account and organization created successfully!' 
          }
        });
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError(error.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-image-section">
          <div className="auth-image-overlay">
            <h1>Welcome to B2B CRM</h1>
            <p>Your complete Customer Relationship Management platform.</p>
          </div>
        </div>
        <div className="auth-form-section">
          <div className="auth-form-container">
            <h2>{isInvitedFlow ? 'Complete Your Registration' : 'Create Account'}</h2>
            <p className="auth-subtitle">Join your organization and start managing customer relationships</p>
            
            {error && <div className="error-message">{error}</div>}
            
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    placeholder="Enter your first name"
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    placeholder="Enter your last name"
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isInvitedFlow}
                  className={`form-control ${isInvitedFlow ? 'disabled' : ''}`}
                />
              </div>

              {/* Only show organization name field if not invited flow */}
              {!isInvitedFlow && (
                <div className="form-group">
                  <label htmlFor="societyName">Organization Name</label>
                  <input
                    type="text"
                    id="societyName"
                    name="societyName"
                    value={formData.societyName}
                    onChange={handleChange}
                    required
                    placeholder="Enter your organization name"
                    className="form-control"
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="Enter your password"
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  placeholder="Confirm your password"
                  className="form-control"
                />
              </div>

              {!isInvitedFlow && (
                <div className="required-fields-note">* Required fields</div>
              )}

              <button type="submit" className="auth-button" disabled={loading}>
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>
            
            <div className="auth-footer">
              <p>Already have an account? 
                <button onClick={() => navigate('/login')} className="auth-link">
                  Login
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup; 